/**
 * Partner Portal — wireframe shell (pipeline, tables, adviser/practice/licensee views).
 * Renamed from ProfessionalPortal.js for alignment with the Partner Portal product name.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './professionalPortalWireframe.css';
import { AdviserAnalyticsView, AdviserSettingsView, PracticeSettingsWireframeView } from './ProfessionalPortalFullWireframeViews.tsx';
import type { RegisteredSoaSummary } from '../../services/adviceApi.ts';
import type { ClientProfileWorkflow } from '../../services/profileApi.ts';
import type { FirmSettings, MessageDefaults } from '../../services/tenantApi.ts';
import { mergeSoaSources } from './soaSources.ts';
import { FirmWorkingPreferences } from './FirmWorkingPreferences.tsx';
import { CapitalMarketAssumptionsSettings } from './CapitalMarketAssumptionsSettings.tsx';
import { FirmTeamSettings } from './FirmTeamSettings.tsx';
import { FirmLicensingSettings } from './FirmLicensingSettings.tsx';
import { ApiAccessSettings } from './ApiAccessSettings.tsx';
import { FirstRunChecklist } from './FirstRunChecklist.tsx';
import { ClientRowActions } from './ClientRowActions.tsx';
import { buildClientsCsv, downloadClientsCsv } from './clientsCsv.ts';
import { ACTIVITY_PRESENTATION, buildActivityFeed } from './activityFeed.ts';
import AnimatedBackground from '../../components/layout/AnimatedBackground.tsx';
import type { DemoCase } from './demoData.tsx';


/** A client engagement row as rendered by this portal — the demo case shape (demoData.tsx) plus the
 *  couple of fields only ever populated by the real (non-demo) case list. */
export interface PartnerCase extends DemoCase {
    /** Whose book this client sits on (client_profiles.adviser_user_id). Null on demo cases. */
    adviserUserId?: string | null;
    /** 'restricted' = the responsible adviser and the principal only. Absent on demo cases. */
    visibility?: 'firm' | 'restricted';
    clientMobile?: string;
    /** A real client file rather than a demo sample — real rows are listed WITHOUT their fact-find
     *  (it is encrypted and only decrypted when that client is opened), which the analytics view
     *  has to account for before drawing conclusions from factFind. */
    isReal?: boolean;
    /** Which saved SOA scenario is "current" for this case, if any (see soaByCaseId below). */
    currentSoaId?: string;
    /** REAL cases only — the server-derived workflow facts behind the pipeline stage. See
     *  pipelineStage.ts; absent on demo cases, which carry their own scripted progress. */
    profileProgressIsEstimated?: boolean;
    profileCompletedSteps?: number | null;
    profileTotalSteps?: number | null;
    savedModelCount?: number;
    soaCount?: number;
    /** The raw server-derived workflow block — what the Activity Feed reads its timestamps from. */
    workflow?: ClientProfileWorkflow;
}

/**
 * How far through the fact-find a client is, in words an adviser can act on.
 *
 * Prefers "3 of 7 steps" over a bare percentage: it tells the adviser how much is left and is
 * verifiable by opening the file, whereas a percentage is not. A percentage the server hasn't
 * actually measured (a file not saved since progress tracking landed) is marked with a leading "~"
 * rather than presented as fact.
 */
/** Why a client is on the follow-up list, and who the ball is with. */
type FollowupKind = 'invite_outstanding' | 'client_stalled' | 'awaiting_modelling' | 'soa_unsent';

interface FollowupRow {
    c: PartnerCase;
    kind: FollowupKind;
    /** Who has to act next. The firm's own queue was previously not shown at all. */
    waitingOn: 'client' | 'firm';
    days: number | null;
}

/** How each follow-up kind presents: what it is, its chip, and the action that clears it. */
const FOLLOWUP_PRESENTATION: Record<FollowupKind, { detail: string; chip: string; chipCls: string; action: string }> = {
    invite_outstanding: { detail: 'Invite outstanding', chip: 'Not Started', chipCls: 'tag t-st', action: 'Send Reminder' },
    client_stalled: { detail: 'Part-way through their profile', chip: 'Stalled', chipCls: 'tag t-pr', action: 'Send Reminder' },
    awaiting_modelling: { detail: 'Profile complete — nothing modelled yet', chip: 'Needs Modelling', chipCls: 'tag t-ok', action: 'Open Model →' },
    soa_unsent: { detail: 'SOA drafted but never sent', chip: 'SOA Unsent', chipCls: 'tag', action: 'Review & Send →' },
};

/**
 * Is this intake form complete enough to submit?
 *
 * Neither modal validated anything. On the invite path that mattered: with a blank email the case
 * creator substituted a placeholder address (`invite.<timestamp>@demo.local`), so the adviser got a
 * client file, a consumed billing seat, and an invite link bound to an address nobody owns — and
 * accepting an invite verifies the signed-in user against the invited address, so that link could
 * never be redeemed. A dead invite that costs a seat is worse than a refused one.
 *
 * The email test is deliberately loose: it rejects a blank or obviously-not-an-address entry and
 * leaves real deliverability to the mail server. Over-strict address validation rejects valid
 * addresses, which is a worse failure than accepting a typo.
 */
function isIntakeReady(firstName: string, email: string, requireEmail: boolean): boolean {
    if (!firstName.trim()) return false;
    if (!requireEmail) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function progressLabel(c: PartnerCase): string {
    const pct = c.profileProgressPct || 0;
    const steps = c.profileCompletedSteps;
    const total = c.profileTotalSteps;
    if (typeof steps === 'number' && typeof total === 'number' && total > 0) {
        return `${steps} of ${total} steps · ${pct}%`;
    }
    return c.profileProgressIsEstimated ? `~${pct}% complete (not yet measured)` : `${pct}% complete`;
}

/** A saved strategy/SOA scenario, as stored against a case (savedCaseStudies prop). */
export interface SoaScenario {
    id: string;
    caseId: string;
    name: string;
    createdAt?: string;
    soaDeliveryStatus?: 'draft' | 'published';
    isCurrentForCase?: boolean;
    /** The advice register's id for this version, recorded when it was registered. */
    serverSoaId?: string;
    /** Present ONLY in the register — there is no local snapshot to reopen or delete. */
    registerOnly?: boolean;
    [key: string]: unknown;
}

export type PartnerRole = 'relationship_owner' | 'practice_owner' | 'licensee';

type IconName = 'grid' | 'users' | 'file' | 'bell' | 'activity' | 'settings' | 'card';

const V = {
    advDash: 'V-adv-dash',
    advFollow: 'V-adv-followups',
    advSoas: 'V-adv-soas',
    advSettings: 'V-adv-settings',
    advAnalytics: 'V-adv-analytics',
    advActivity: 'V-adv-activity',
    pracDash: 'V-prac-dash',
    pracClients: 'V-prac-clients',
    pracFollow: 'V-prac-followups',
    pracSettings: 'V-prac-settings',
    licDash: 'V-lic-dash',
    licAdvisers: 'V-lic-advisers',
    licBilling: 'V-lic-billing',
};

function NavIcon({ name }: { name: IconName }) {
    const s = { width: 15, height: 15, stroke: 'currentColor', fill: 'none', strokeWidth: 2, flexShrink: 0 };
    switch (name) {
        case 'grid':
            return (
                <svg viewBox="0 0 24 24" style={s}>
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
            );
        case 'users':
            return (
                <svg viewBox="0 0 24 24" style={s}>
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                </svg>
            );
        case 'file':
            return (
                <svg viewBox="0 0 24 24" style={s}>
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                </svg>
            );
        case 'bell':
            return (
                <svg viewBox="0 0 24 24" style={s}>
                    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 01-3.46 0" />
                </svg>
            );
        case 'activity':
            return (
                <svg viewBox="0 0 24 24" style={s}>
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
            );
        case 'settings':
            return (
                <svg viewBox="0 0 24 24" style={s}>
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
                </svg>
            );
        case 'card':
            return (
                <svg viewBox="0 0 24 24" style={s}>
                    <rect x="1" y="4" width="22" height="16" rx="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
            );
        default:
            return null;
    }
}

function daysSinceIso(iso: string | null | undefined): number | null {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    return Math.floor((Date.now() - t) / 86400000);
}

function formatShortDate(value: string | null | undefined): string {
    if (!value) return '—';
    return new Date(value).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Prefer submission date; fall back to last activity so pipeline never shows a bare "—" when activity exists. */
function formatSubmittedLabel(c: PartnerCase): string {
    return formatShortDate(c.submissionDate || c.lastActivityAt);
}

function formatLastActivityDisplay(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = daysSinceIso(iso);
    const dateStr = new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    if (d === 0) return `${dateStr} · Today`;
    if (d === 1) return `${dateStr} · Yesterday`;
    return `${dateStr} · ${d} days ago`;
}

type ProfileFilter = 'all' | 'invited' | 'started' | 'complete';
type ActivityFilter = 'all' | 'today' | 'week' | 'month' | 'older';
type PipelineFilter = 'all' | 'overdue' | 'ready';

function caseMatchesProfileFilter(c: PartnerCase, f: ProfileFilter): boolean {
    if (f === 'all') return true;
    // 'draft' folded in for the same reason the pipeline column went: no intake path produces it,
    // but demo cases still carry it, and a filter must not make a case disappear entirely.
    if (f === 'invited') return c.status === 'invited' || c.status === 'draft';
    if (f === 'started') return c.status === 'started';
    if (f === 'complete') return ['submitted', 'adviser_review', 'soa_draft', 'soa_published'].includes(c.status);
    return true;
}

function caseMatchesActivityFilter(c: PartnerCase, f: ActivityFilter): boolean {
    if (f === 'all') return true;
    const d = daysSinceIso(c.lastActivityAt);
    if (d == null) return false;
    if (f === 'today') return d === 0;
    if (f === 'week') return d <= 7;
    if (f === 'month') return d <= 30;
    if (f === 'older') return d > 30;
    return true;
}

function accessModeLabel(mode: string): string {
    if (mode === 'client_stop_dashboard') return 'Adviser-led';
    if (mode === 'client_end_to_end') return 'Self-serve';
    return 'In-person';
}

function accessModeClass(mode: string): string {
    if (mode === 'client_stop_dashboard' || mode === 'in_person') return 'mtag mt-adv';
    return 'mtag mt-self';
}

function profileTag(caseRow: PartnerCase): { cls: string; label: string } {
    const { status, profileProgressPct } = caseRow;
    if (status === 'draft') return { cls: 'tag t-dr', label: 'Not invited' };
    if (status === 'invited') return { cls: 'tag t-sn', label: 'Invite sent' };
    if (status === 'started') {
        const estimated = caseRow.profileProgressIsEstimated ? '~' : '';
        return { cls: 'tag t-pr', label: `● In Progress ${estimated}${profileProgressPct || 0}%` };
    }
    if (['submitted', 'adviser_review', 'soa_draft', 'soa_published'].includes(status)) return { cls: 'tag t-ok', label: '● Complete' };
    return { cls: 'tag t-dr', label: status };
}

function soaTag(caseRow: PartnerCase): { cls: string; style?: React.CSSProperties; label: string } {
    if (caseRow.soaStatus === 'published') return { cls: 'tag t-ok', label: '● Published' };
    if (caseRow.soaStatus === 'draft') return { cls: 'tag', style: { background: '#fef9c3', color: '#854d0e' }, label: '● Draft — unsent' };
    return { cls: 'tag', style: { background: '#f1f5f9', color: '#64748b' }, label: '— No SOA yet' };
}

/** Review (adviser SOA workflow) only when there is something to review in the portal. */
function hasReviewableSoa(caseRow: PartnerCase): boolean {
    return caseRow.soaStatus === 'draft' || caseRow.soaStatus === 'published';
}

type BadgeKey = 'clients' | 'followups' | 'practices' | 'advisers';

interface NavSection {
    sect: string;
    id?: undefined;
}
interface NavLink {
    sect?: undefined;
    id: string;
    icon: IconName;
    label: string;
    badgeKey?: BadgeKey;
    badgeCls?: string;
}
type NavItem = NavSection | NavLink;

interface TabItem {
    id: string;
    label: string;
    badge?: string | null;
}

interface PortalCfg {
    key: string;
    lvlLabel: string;
    lvlCls: string;
    ctxName: string;
    ctxSub: string;
    avaInit: string;
    avaColor: string;
    uname: string;
    urole: string;
    tabs: TabItem[];
    nav: NavItem[];
    topInvite: boolean;
    topInPerson: boolean;
    topPracticeExtras?: boolean;
    topLicensee?: boolean;
}

const PORTAL_CFG: Record<PartnerRole, PortalCfg> = {
    relationship_owner: {
        key: 'adv',
        lvlLabel: 'Adviser View',
        lvlCls: 'lv-adv',
        ctxName: 'Joncarl La Rosa',
        ctxSub: 'CFP · La Rosa & Partners',
        avaInit: 'JL',
        avaColor: 'linear-gradient(135deg,#1a6fd4,#17b8e0)',
        uname: 'Joncarl La Rosa',
        urole: 'Financial Adviser · CFP',
        tabs: [
            { id: V.advDash, label: 'Dashboard' },
            { id: V.advFollow, label: 'Follow-ups', badge: null },
            { id: V.advSoas, label: 'Reports & SOAs' },
            { id: V.advSettings, label: 'Settings' },
            { id: V.advAnalytics, label: 'Analytics' },
        ],
        nav: [
            { sect: 'My Workspace' },
            { id: V.advDash, icon: 'grid', label: 'Dashboard' },
            { id: V.advDash, icon: 'users', label: 'My Clients', badgeKey: 'clients' },
            { id: V.advSoas, icon: 'file', label: 'Reports & SOAs' },
            { sect: 'Actions' },
            { id: V.advFollow, icon: 'bell', label: 'Follow-ups', badgeKey: 'followups', badgeCls: 'am' },
            { id: V.advActivity, icon: 'activity', label: 'Activity Feed' },
            { sect: 'Profile' },
            { id: V.advSettings, icon: 'settings', label: 'Settings' },
            { id: V.advAnalytics, icon: 'activity', label: 'Analytics' },
        ],
        topInvite: true,
        topInPerson: true,
    },
    practice_owner: {
        key: 'prac',
        lvlLabel: 'Practice View',
        lvlCls: 'lv-prac',
        ctxName: 'La Rosa & Partners',
        ctxSub: 'AFSL 482910 · Sydney',
        avaInit: 'LR',
        avaColor: 'linear-gradient(135deg,#1a6fd4,#17b8e0)',
        uname: 'Practice Admin',
        urole: 'Firm Administrator',
        tabs: [
            { id: V.pracDash, label: 'Dashboard' },
            { id: V.pracClients, label: 'All Clients' },
            { id: V.pracFollow, label: 'Follow-ups', badge: null },
            { id: V.pracSettings, label: 'Settings' },
        ],
        nav: [
            { sect: 'Overview' },
            { id: V.pracDash, icon: 'grid', label: 'Dashboard' },
            { id: V.pracClients, icon: 'users', label: 'All Clients', badgeKey: 'clients' },
            { sect: 'Actions' },
            { id: V.pracFollow, icon: 'bell', label: 'Follow-ups', badgeKey: 'followups', badgeCls: 'am' },
            { id: V.pracDash, icon: 'activity', label: 'Activity Feed' },
            { sect: 'Practice' },
            { id: V.pracSettings, icon: 'settings', label: 'Settings' },
            { id: V.pracSettings, icon: 'card', label: 'Billing' },
        ],
        topInvite: true,
        topInPerson: true,
        topPracticeExtras: true,
    },
    licensee: {
        key: 'lic',
        lvlLabel: 'Licensee View',
        lvlCls: 'lv-lic',
        ctxName: 'Demo Licensee Group',
        ctxSub: 'AFSL Group · multiple practices',
        avaInit: 'LG',
        avaColor: 'linear-gradient(135deg,#7c3aed,#a78bfa)',
        uname: 'Licensee Admin',
        urole: 'AFSL Group Administrator',
        tabs: [
            { id: V.licDash, label: 'All Practices' },
            { id: V.licAdvisers, label: 'All Advisers' },
            { id: V.licBilling, label: 'Billing' },
        ],
        nav: [
            { sect: 'Overview' },
            { id: V.licDash, icon: 'grid', label: 'All Practices', badgeKey: 'practices', badgeCls: 'pu' },
            { id: V.licAdvisers, icon: 'users', label: 'All Advisers', badgeKey: 'advisers' },
            { sect: 'Management' },
            { id: V.licBilling, icon: 'card', label: 'Billing & Plans' },
            { id: V.licDash, icon: 'activity', label: 'Platform Analytics' },
            { sect: 'Admin' },
            { id: V.licDash, icon: 'settings', label: 'Licensee Settings' },
        ],
        topInvite: false,
        topInPerson: false,
        topLicensee: true,
    },
};

const VIEW_TITLES: Record<string, string> = {
    [V.advDash]: 'My Dashboard',
    [V.advFollow]: 'Follow-ups',
    [V.advSoas]: 'Reports & SOAs',
    [V.advSettings]: 'Settings',
    [V.advAnalytics]: 'Analytics',
    [V.pracDash]: 'Practice Dashboard',
    [V.pracClients]: 'All Clients',
    [V.pracFollow]: 'Follow-up Centre',
    [V.pracSettings]: 'Practice Settings',
    [V.licDash]: 'All Practices',
    [V.licBilling]: 'Billing & Subscriptions',
    [V.licAdvisers]: 'All Advisers',
};

interface PipelineCardProps {
    title: React.ReactNode;
    subtitle: React.ReactNode;
    children?: React.ReactNode;
    highlight?: boolean;
}

function PipelineCard({ title, subtitle, children, highlight }: PipelineCardProps) {
    return (
        <div className={`cc${highlight ? ' hi' : ''}`}>
            <div className="ccn">{title}</div>
            <div className="ccm">{subtitle}</div>
            {children}
        </div>
    );
}

/** REAL mode client-billing summary — see PartnerPortalProps.billingSummary. */
export interface BillingSummary {
    mode?: 'annual' | 'payg' | 'comp' | null;
    used?: number;
    remaining?: number;
    includedBase?: number;
    capacity?: number;
}

interface BillingCardProps {
    summary: BillingSummary | null | undefined;
    onManage?: () => void;
    onUpgrade?: () => void;
    onBuyClient?: () => void;
}

/** Real-mode Settings billing card: current plan, client usage, and the owner's billing actions. */
function BillingCard({ summary, onManage, onUpgrade, onBuyClient }: BillingCardProps) {
    const { mode, used, remaining, includedBase } = summary || {};
    const planLabel =
        mode === 'annual' ? 'Annual plan' : mode === 'payg' ? 'Pay per client' : mode === 'comp' ? 'Free access' : 'No plan';
    const planPrice =
        mode === 'annual' ? '$800 + GST / year' : mode === 'payg' ? '$336 + GST per client' : mode === 'comp' ? 'Complimentary' : '';
    const btn = {
        base: { border: 'none', borderRadius: 10, padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
        primary: { background: 'linear-gradient(90deg,#1a6fd4,#17b8e0)', color: '#fff' },
        outline: { background: '#fff', color: '#1a6fd4', border: '2px solid #1a6fd4' },
        ghost: { background: '#f1f5f9', color: '#475569' },
    };
    return (
        <div style={{ marginTop: 14, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(15,23,42,.06)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: '#94a3b8', marginBottom: 12 }}>Billing</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16 }}>
                <span style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{planLabel}</span>
                <span style={{ fontSize: 13.5, color: '#64748b', fontWeight: 600 }}>{planPrice}</span>
            </div>
            {mode !== 'comp' && (
                <div style={{ marginTop: 6, fontSize: 13.5, color: '#475569' }}>
                    {used} client{used === 1 ? '' : 's'} taken on
                    {mode === 'annual'
                        ? ` · ${includedBase} included${(remaining ?? 0) > 0 ? `, ${remaining} left before the $70 per-client fee` : ' — extra clients are $70 + GST each'}`
                        : remaining == null
                          // "No plan" has no allowance to count down, and printing the raw value
                          // there rendered "undefined paid clients remaining" on the firm's own
                          // settings page.
                          ? ''
                          : ` · ${remaining} paid client${remaining === 1 ? '' : 's'} remaining`}
                </div>
            )}
            {mode === 'comp' && (
                <div style={{ marginTop: 6, fontSize: 13.5, color: '#475569' }}>Your firm has complimentary access — no charges apply.</div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 16 }}>
                {mode === 'payg' && onBuyClient && (
                    <button type="button" onClick={onBuyClient} style={{ ...btn.base, ...btn.primary }}>Add a client — $336</button>
                )}
                {mode === 'payg' && onUpgrade && (
                    <button type="button" onClick={onUpgrade} style={{ ...btn.base, ...btn.outline }}>Switch to annual — $800/yr</button>
                )}
                {onManage && mode !== 'comp' && (
                    <button type="button" onClick={onManage} style={{ ...btn.base, ...btn.ghost }}>Manage billing &amp; invoices</button>
                )}
            </div>
        </div>
    );
}

interface RealIdentity {
    adviserName: string;
    firmName: string;
    initials: string;
    roleLine: string;
    licenceLine: string;
    email?: string;
    /** Which firm, who I am in it, and what I may do — see useProfessionalPortal.ts. */
    tenantId?: string;
    userId?: string;
    role?: 'owner' | 'adviser' | 'staff' | 'client';
    /** Has the firm said how its advice is provided? Drives the first-run checklist. */
    firmProfileComplete?: boolean;
    /** Only 'adviser' gives its own advice; the licensing questions exist for nobody else. */
    profession?: string | null;
    /** What this firm calls the people who hold clients. */
    holderLabel?: string;
}

export interface InviteCaseDetails {
    completingAs: string;
    firstName: string;
    lastName: string;
    /** When someone other than the client completes the form: their relationship to the client. */
    relationship?: string;
    email: string;
    mobile: string;
    accessMode: string;
    autoFollowUp: string;
}

export interface InPersonCaseDetails {
    firstName: string;
    lastName: string;
    email: string;
    preferredPhone: string;
    completingAs: string;
}

interface PartnerPortalProps {
    role: PartnerRole;
    setRole: (role: PartnerRole) => void;
    cases: PartnerCase[];
    savedCaseStudies?: SoaScenario[];
    onCreateInPersonCase?: (details: InPersonCaseDetails) => void;
    onCreateInviteCase?: (details: InviteCaseDetails) => void;
    onOpenCaseJourney: (id: string) => void;
    onOpenCaseFromModel: (id: string) => void;
    /** Advice-register versions per REAL profile id (adviceApi.listSoas). The register is the legal
     *  record; savedCaseStudies below is only the in-app working copy, which is why an SOA used to
     *  disappear from this list at sign-out. */
    registeredSoasByProfileId?: Record<string, RegisteredSoaSummary[]>;
    /** Resend a client's invite link (a fresh one-time link, emailed). Absent in demo mode. */
    onRemindClient?: (caseId: string) => void;
    /** The firm's working preferences — the invite form pre-sets its follow-up choice from this. */
    firmSettings?: FirmSettings;
    /** Whether this server actually chases clients, and its cap. Null while the firm is resolving. */
    autoReminders?: { enabled: boolean; maxPerClient: number } | null;
    messageDefaults?: MessageDefaults | null;
    onSaveFirmSettings?: (patch: FirmSettings) => Promise<boolean>;
    /** Analytics is built from fact-find content, which the client list doesn't carry — see
     *  loadFactFindsForAnalytics. Called when the adviser opens the tab. */
    analyticsDataState?: 'idle' | 'loading' | 'loaded' | 'partial';
    onNeedAnalyticsData?: () => void;
    /** Re-read the firm's client list after a file changes hands or changes visibility. */
    onClientAssignmentChanged?: () => void;
    onMarkSoaSent?: (caseId: string, soaId: string) => void;
    onSetCurrentSoaForCase?: (caseId: string, soaId: string) => void;
    onDeleteDraftSoa?: (soaId: string) => void;
    onExitToConsumer?: () => void;
    /** Platform owner only: open the admin console from the portal sidebar (never a floating pill). */
    onOpenAdmin?: () => void;
    /** When incremented (e.g. Review clicked), navigate to Reports & SOAs. */
    reviewNavigationNonce?: number;
    /** Highlights this case in the SOA list when set. */
    focusedCaseId?: string | null;
    /** REAL mode: { adviserName, firmName, initials, roleLine, licenceLine, email } — replaces
     *  the demo identity chrome and skips the wireframe login (the user is genuinely signed in). */
    realIdentity?: RealIdentity | null;
    /** True while a signed-in firm member's workspace is still resolving — show a branded loading
     *  state, NEVER the demo login wireframe (owner-reported race on real-firm entry). */
    realModeLoading?: boolean;
    /** REAL mode client-billing summary { mode, capacity, used, remaining, ... } or null (billing off). */
    billingSummary?: BillingSummary | null;
    /** Owner-only billing actions (Settings panel): manage via Stripe, upgrade to annual, buy a slot. */
    onManageBilling?: () => void;
    onUpgradeAnnual?: () => void;
    onBuyClient?: () => void;
}

export default function PartnerPortal({
    role,
    // `setRole` is still on the props (App passes it) but nothing here reads it: its only caller was
    // the demo login's role picker, removed 29 Sep 2026. The real role comes from the firm membership.
    cases,
    savedCaseStudies = [],
    onCreateInPersonCase,
    onCreateInviteCase,
    onOpenCaseJourney,
    onOpenCaseFromModel,
    registeredSoasByProfileId,
    onRemindClient,
    firmSettings,
    autoReminders,
    messageDefaults,
    onSaveFirmSettings,
    analyticsDataState = 'idle',
    onNeedAnalyticsData,
    onClientAssignmentChanged,
    onMarkSoaSent,
    onSetCurrentSoaForCase,
    onDeleteDraftSoa,
    onExitToConsumer,
    onOpenAdmin,
    reviewNavigationNonce = 0,
    focusedCaseId = null,
    realIdentity = null,
    realModeLoading = false,
    billingSummary = null,
    onManageBilling,
    onUpgradeAnnual,
    onBuyClient,
}: PartnerPortalProps) {
    /*
     * THE DEMO LOGIN AND DEMO CODE SCREENS ARE GONE (29 Sep 2026, owner-reported).
     *
     * The portal used to carry its own wireframe sign-in and a fake "SMS code" screen, chosen by a
     * `stage` flag that started as 'login' and only switched to 'portal' in an effect once the real
     * workspace arrived. Effects run AFTER React paints, so on the render where the firm's identity
     * landed, the loading guard below stopped applying while `stage` still said 'login' — and the
     * demo sign-in was drawn for one frame before the effect flipped it. That was the split-second
     * "old demo authentication screen" the owner saw on some logins.
     *
     * Nothing reaches this component signed out any more: every route into the portal comes after a
     * real sign-in, and Sign out leaves the portal rather than returning to a portal login. So the
     * screens were only ever seen as that flash. Removing them removes the flash by construction;
     * real authentication, including the authenticator-app second factor, lives in AccountModal.
     */
    const [search, setSearch] = useState('');
    const [activeView, setActiveView] = useState(V.advDash);
    // Analytics needs every client's fact-find, which the list deliberately omits. Fetch it the
    // moment the adviser actually opens the tab, not before — most sessions never go there.
    useEffect(() => {
        if (activeView === V.advAnalytics) onNeedAnalyticsData?.();
    }, [activeView, onNeedAnalyticsData]);
    const [navActiveId, setNavActiveId] = useState(V.advDash);
    const [modal, setModal] = useState<'invite' | 'inperson' | 'adduser' | 'addprac' | null>(null);

    /*
     * Whether this firm has waved away the setup reminders.
     *
     * Per firm, per browser, and deliberately not on the server: it is a nudge, not a record, and a
     * preference this small does not justify a column or a round trip. Wrapped because storage throws
     * outright in a private window, and a dashboard must not fail to render over a dismissed hint.
     */
    const setupDismissKey = realIdentity?.tenantId ? `acw.setupDismissed.${realIdentity.tenantId}` : null;
    const [setupDismissed, setSetupDismissed] = useState(() => {
        if (!setupDismissKey) return false;
        try {
            return window.localStorage.getItem(setupDismissKey) === '1';
        } catch {
            return false;
        }
    });
    const dismissSetup = () => {
        setSetupDismissed(true);
        try {
            if (setupDismissKey) window.localStorage.setItem(setupDismissKey, '1');
        } catch {
            // Dismissed for this visit; it returns next time. Better than a broken click.
        }
    };
    const [inviteCompletingAs, setInviteCompletingAs] = useState('self');
    const [inviteFirstName, setInviteFirstName] = useState('');
    const [inviteLastName, setInviteLastName] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    // When someone else completes the form: their relationship to the client.
    const [inviteRelationship, setInviteRelationship] = useState('');
    /** Why Send Invite did nothing, said under the button (owner, 16 Oct 2026: a disabled button looked live). */
    const [inviteProblem, setInviteProblem] = useState<string | null>(null);
    const [inviteMobile, setInviteMobile] = useState('');
    const [inviteAccessMode, setInviteAccessMode] = useState('client_stop_dashboard');
    // Pre-set from the firm's own preference rather than a hardcoded 7 days, so an adviser who has
    // decided how their practice chases isn't re-answering it for every client. 'manual' when the
    // firm has chosen not to chase automatically.
    const firmDefaultFollowUp = firmSettings?.defaultFollowUpDays;
    const [inviteAutoFollowUp, setInviteAutoFollowUp] = useState(
        firmDefaultFollowUp === null ? 'manual' : String(firmDefaultFollowUp ?? 7),
    );
    const [inPersonFirstName, setInPersonFirstName] = useState('');
    const [inPersonLastName, setInPersonLastName] = useState('');
    const [inPersonEmail, setInPersonEmail] = useState('');
    const [inPersonPhone, setInPersonPhone] = useState('');
    const [inPersonCompletingAs, setInPersonCompletingAs] = useState('self');
    const [filterProfile, setFilterProfile] = useState<ProfileFilter>('all');
    const [filterActivity, setFilterActivity] = useState<ActivityFilter>('all');
    const [pipelineFilter, setPipelineFilter] = useState<PipelineFilter>('all');

    const baseCfg = PORTAL_CFG[role] || PORTAL_CFG.relationship_owner;
    // Real mode: the signed-in adviser + their firm replace every piece of demo identity chrome.
    const cfg = realIdentity
        ? {
              ...baseCfg,
              ctxName: realIdentity.adviserName,
              ctxSub: realIdentity.firmName,
              avaInit: realIdentity.initials,
              uname: realIdentity.adviserName,
              urole: realIdentity.roleLine,
          }
        : baseCfg;

    /** Real-mode Settings: the firm's actual profile (replaces the demo rosters entirely). */
    const realFirmSettings = realIdentity && (
        <>
            <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 18 }}>Firm profile</h2>
            <div style={{ maxWidth: 560 }}>
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(15,23,42,.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
                        <div style={{ width: 46, height: 46, borderRadius: 12, background: 'linear-gradient(135deg,#1a6fd4,#17b8e0)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 17 }}>
                            {realIdentity.initials}
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{realIdentity.firmName}</div>
                            <div style={{ fontSize: 13, color: '#64748b' }}>{realIdentity.licenceLine}</div>
                        </div>
                    </div>
                    {[
                        ['Owner', `${realIdentity.adviserName}${realIdentity.email ? ` · ${realIdentity.email}` : ''}`],
                        ['Role', realIdentity.roleLine],
                        ['Licensing', realIdentity.licenceLine],
                    ].map(([k, v]) => (
                        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '10px 0', borderTop: '1px solid #f1f5f9', fontSize: 13.5 }}>
                            <span style={{ color: '#64748b' }}>{k}</span>
                            <span style={{ color: '#0f172a', fontWeight: 600, textAlign: 'right' }}>{v}</span>
                        </div>
                    ))}
                </div>
                {billingSummary && <BillingCard summary={billingSummary} onManage={onManageBilling} onUpgrade={onUpgradeAnnual} onBuyClient={onBuyClient} />}
                {/* The settings that change what the system DOES. These were first written into the
                    DEMO settings view only, so a signed-in firm could never reach them. */}
                <FirmWorkingPreferences settings={firmSettings} messageDefaults={messageDefaults} onSave={onSaveFirmSettings} />
                {/* Your team. Only with a real firm behind it: the demo identity has no tenant to
                    manage, and a team screen that cannot load a team is worse than none. */}
                {/* Only an adviser firm has a licence of its own to describe. For everyone else the advice
                    is AgedCareWise's, and a question about "how your advice is provided" would invite a
                    claim the partner terms say they must not make. */}
                {realIdentity.tenantId && realIdentity.userId && realIdentity.profession === 'adviser' && (
                    <FirmLicensingSettings
                        tenantId={realIdentity.tenantId}
                        myUserId={realIdentity.userId}
                        isPrincipal={realIdentity.role === 'owner'}
                    />
                )}
                {realIdentity.tenantId && realIdentity.userId && (
                    <FirmTeamSettings
                        tenantId={realIdentity.tenantId}
                        myUserId={realIdentity.userId}
                        isPrincipal={realIdentity.role === 'owner'}
                        holderLabel={realIdentity.holderLabel}
                    />
                )}
                {/* The REST API documentation, and the client_secret a partner's own systems sign in
                    with. Shown to anyone signed in as a real firm, not just the principal: the
                    credential is PERSONAL — it carries the access of whoever minted it and nobody
                    else's — so an adviser gating their own key behind the owner would be gating it
                    behind someone whose key it could never be. */}
                <ApiAccessSettings />
                <div style={{ marginTop: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 16px', fontSize: 13, color: '#64748b', lineHeight: 1.55 }}>
                    Firm branding is on its way — it'll appear here as it goes live.
                </div>
            </div>
            {/* The firm's own capital market assumptions — locked here by the firm, applied to every
                projection its advisers and clients run, disclosed on advice. OUTSIDE the 560px
                profile column: this one is a table, and squeezing it there pushed its derived
                columns off the card. */}
            <div style={{ maxWidth: 820, marginTop: 18 }}>
                {/* Only a firm that gives its own advice owns the assumptions it rests on. For everyone
                    else the advice is AgedCareWise's, and so are the figures; the server refuses a
                    save from such a firm too (tenants.ts). */}
                {realIdentity.profession === 'adviser' ? (
                    <CapitalMarketAssumptionsSettings settings={firmSettings} onSave={onSaveFirmSettings} firmName={realIdentity.firmName} setBy={realIdentity.adviserName} />
                ) : (
                    <div style={{ marginTop: 14, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(15,23,42,.06)' }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>Capital market assumptions</div>
                        <p style={{ fontSize: 13, color: '#64748b', margin: '6px 0 0', lineHeight: 1.55, maxWidth: 720 }}>
                            AgedCareWise gives the advice for your firm&apos;s clients, so every projection runs on AgedCareWise&apos;s
                            own assumptions — you can read them on the Assumptions page. A client can still set a rate on an
                            individual asset in their profile; the advice document reports that as their decision and shows
                            what the same plan looks like at AgedCareWise&apos;s rates.
                        </p>
                    </div>
                )}
            </div>
        </>
    );

    const metrics = useMemo(() => {
        const total = cases.length;
        const active = cases.filter((c) => c.status !== 'draft').length;
        const invited = cases.filter((c) => c.status === 'invited').length;
        const started = cases.filter((c) => c.status === 'started').length;
        const awaiting = invited + started;
        const readyStatuses = ['submitted', 'adviser_review'];
        const ready = cases.filter((c) => readyStatuses.includes(c.status)).length;
        // Split the "ready" queue: a submitted profile needs an adviser to PICK IT UP, whereas one
        // already in modelling is being worked. Lumping them together hid the queue that needs action.
        const awaitingModelling = cases.filter((c) => c.status === 'submitted').length;
        const inModelling = cases.filter((c) => c.status === 'adviser_review').length;
        const soaPub = cases.filter((c) => c.soaStatus === 'published').length;
        // Drafted-but-unsent advice was invisible: the only SOA tile counted published, so an SOA
        // sitting in draft (the reported Tom Smith case) showed up nowhere on the dashboard.
        const soaDraft = cases.filter((c) => c.soaStatus === 'draft').length;
        // Same definition as followupRows below, so the tab badge matches what the tab lists.
        const followups = cases.filter(
            (c) => c.soaStatus === 'draft' || c.status === 'submitted' || c.status === 'invited' || c.status === 'started',
        ).length;
        return { total, active, invited, started, awaiting, ready, awaitingModelling, inModelling, soaPub, soaDraft, followups };
    }, [cases]);

    const filteredCases = useMemo(() => {
        const q = search.trim().toLowerCase();
        return cases.filter((c) => !q || c.clientName.toLowerCase().includes(q) || (c.clientEmail || '').toLowerCase().includes(q));
    }, [cases, search]);

    const clientTableFilteredCases = useMemo(() => {
        return filteredCases.filter((c) => caseMatchesProfileFilter(c, filterProfile) && caseMatchesActivityFilter(c, filterActivity));
    }, [filteredCases, filterProfile, filterActivity]);

    const pipelineScopedCases = useMemo(() => {
        if (pipelineFilter === 'ready') {
            return filteredCases.filter((c) => ['submitted', 'adviser_review'].includes(c.status));
        }
        if (pipelineFilter === 'overdue') {
            return filteredCases.filter((c) => {
                const d = daysSinceIso(c.lastActivityAt);
                if (d == null) return false;
                // Waiting on the client.
                if (c.status === 'invited' && d > 5) return true;
                if (c.status === 'started' && (c.profileProgressPct || 0) < 80 && d > 3) return true;
                // Waiting on US. A finished profile nobody has picked up, or an SOA drafted and never
                // sent, is a client waiting on the firm — previously never counted as overdue at all,
                // so the only work this filter could surface was somebody else's.
                if (c.status === 'submitted' && d > 3) return true;
                return c.soaStatus === 'draft' && d > 5;
            });
        }
        return filteredCases;
    }, [filteredCases, pipelineFilter]);
    // 'draft' is folded in: no real intake path produces it (see the pipeline markup), but demo
    // cases still carry it, and a case that exists in state must appear SOMEWHERE.
    const pipelineSentCases = useMemo(
        () => pipelineScopedCases.filter((c) => c.status === 'invited' || c.status === 'draft'),
        [pipelineScopedCases],
    );
    const pipelineProgressCases = useMemo(() => pipelineScopedCases.filter((c) => c.status === 'started'), [pipelineScopedCases]);
    const pipelineReadyCases = useMemo(
        () => pipelineScopedCases.filter((c) => ['submitted', 'adviser_review'].includes(c.status)),
        [pipelineScopedCases]
    );
    const pipelineSoaCases = useMemo(
        () => pipelineScopedCases.filter((c) => c.soaStatus === 'draft' || c.soaStatus === 'published' || c.status === 'soa_draft' || c.status === 'soa_published'),
        [pipelineScopedCases]
    );
    /**
     * What needs attention, on BOTH sides of the engagement.
     *
     * This used to list only clients — invited-but-not-started, or stalled part-way through the
     * fact-find — so work sitting with the FIRM was invisible: a finished profile nobody had picked
     * up, or an SOA drafted weeks ago and never sent. Those are the items a client is actually
     * waiting on, and they never appeared anywhere in the portal.
     */
    const followupRows = useMemo<FollowupRow[]>(() => {
        const rows: FollowupRow[] = filteredCases.map((c) => {
            const days = daysSinceIso(c.lastActivityAt);
            // Furthest-along first: an unsent SOA is a more pressing obligation than an unstarted invite.
            if (c.soaStatus === 'draft') {
                return { c, kind: 'soa_unsent', waitingOn: 'firm', days };
            }
            if (c.status === 'submitted') {
                return { c, kind: 'awaiting_modelling', waitingOn: 'firm', days };
            }
            if (c.status === 'invited') {
                return { c, kind: 'invite_outstanding', waitingOn: 'client', days };
            }
            if (c.status === 'started' && (c.profileProgressPct || 0) < 80) {
                return { c, kind: 'client_stalled', waitingOn: 'client', days };
            }
            return null;
        }).filter((r): r is FollowupRow => r !== null);
        // Longest-waiting first — the whole point of the list is what has been ignored longest.
        return rows.sort((a, b) => (b.days ?? -1) - (a.days ?? -1));
    }, [filteredCases]);
    /** Working copies (sessionStorage) reconciled with the advice register — see soaSources.ts. */
    const soaByCaseId = useMemo(
        () => mergeSoaSources(savedCaseStudies, registeredSoasByProfileId),
        [savedCaseStudies, registeredSoasByProfileId],
    );

    /**
     * Download the client table as CSV — exactly the rows and columns on screen, honouring the
     * current search and filters, so what you export is what you were looking at.
     *
     * This button previously did nothing at all. No client FINANCIAL data is included: this is the
     * workflow view (who, what stage, how far through, when last active), which is what an adviser
     * wants a list of. Built in the browser from data already loaded — nothing is sent anywhere.
     */
    /** Download the client table as CSV. This button previously did nothing. See clientsCsv.ts. */
    const exportClientsCsv = useCallback(() => {
        const csv = buildClientsCsv(clientTableFilteredCases, (c) => profileTag(c).label.replace(/^[^A-Za-z]+/, ''));
        downloadClientsCsv(csv, 'clients-' + new Date().toISOString().slice(0, 10) + '.csv');
    }, [clientTableFilteredCases]);

    const clientTableFilterBar = (
        <div
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 10,
                marginBottom: 12,
                padding: '10px 12px',
                background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                border: '1px solid var(--bdr)',
                borderRadius: 'var(--rl)',
            }}
        >
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>Filters</span>
            <select
                className="fus"
                value={filterProfile}
                onChange={(e) => setFilterProfile(e.target.value as ProfileFilter)}
                aria-label="Filter by profile stage"
                style={{ minWidth: 168 }}
            >
                <option value="all">All profile stages</option>
                <option value="draft">Not invited yet</option>
                <option value="invited">Sent (not started)</option>
                <option value="started">In progress</option>
                <option value="complete">Profile complete</option>
            </select>
            <select
                className="fus"
                value={filterActivity}
                onChange={(e) => setFilterActivity(e.target.value as ActivityFilter)}
                aria-label="Filter by last activity"
                style={{ minWidth: 176 }}
            >
                <option value="all">Any last activity</option>
                <option value="today">Active today</option>
                <option value="week">Last 7 days</option>
                <option value="month">Last 30 days</option>
                <option value="older">30+ days ago</option>
            </select>
            {(filterProfile !== 'all' || filterActivity !== 'all') && (
                <button
                    type="button"
                    className="bsm bg"
                    style={{ fontSize: 12, padding: '5px 11px' }}
                    onClick={() => {
                        setFilterProfile('all');
                        setFilterActivity('all');
                    }}
                >
                    Clear filters
                </button>
            )}
            <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 'auto' }}>{clientTableFilteredCases.length} shown</span>
        </div>
    );

    useEffect(() => {
        const first = PORTAL_CFG[role]?.tabs[0]?.id || V.advDash;
        setActiveView(first);
        setNavActiveId(first);
    }, [role]);

    const setView = useCallback((id: string) => {
        setActiveView(id);
        setNavActiveId(id);
    }, []);

    useEffect(() => {
        if (!reviewNavigationNonce) return;
        if (role === 'relationship_owner') {
            setView(V.advSoas);
            return;
        }
        if (role === 'practice_owner') {
            setView(V.pracClients);
            return;
        }
        setView(V.licDash);
    }, [reviewNavigationNonce, role, setView]);

    const badgeCounts = useMemo(
        () => ({
            clients: metrics.total,
            followups: metrics.followups,
            practices: 8,
            advisers: 34,
        }),
        [metrics]
    );

    const tabsWithBadges = useMemo(() => {
        return cfg.tabs.map((t) => {
            if (t.id === V.advFollow || t.id === V.pracFollow) return { ...t, badge: metrics.followups > 0 ? String(metrics.followups) : null };
            return t;
        });
    }, [cfg.tabs, metrics.followups]);

    const resetInviteForm = () => {
        setInviteProblem(null);
        setInviteCompletingAs('self');
        setInviteFirstName('');
        setInviteLastName('');
        setInviteRelationship('');
        setInviteEmail('');
        setInviteMobile('');
        setInviteAccessMode('client_stop_dashboard');
        setInviteAutoFollowUp('7');
    };

    const resetInPersonForm = () => {
        setInPersonFirstName('');
        setInPersonLastName('');
        setInPersonEmail('');
        setInPersonPhone('');
        setInPersonCompletingAs('self');
    };

    const renderInviteModal = () => (
        <div className={`mdrop${modal === 'invite' ? ' on' : ''}`} role="presentation" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
            <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="mclose" onClick={() => setModal(null)} aria-label="Close">
                    ×
                </button>
                <div className="mt">Invite a New Client</div>
                <div className="ms">A secure link is sent so the person can complete the profile remotely before your meeting.</div>
                <div className="mfg">
                    <label className="mfl" htmlFor="pp-invite-completer">
                        Who will be completing this form?
                    </label>
                    <select
                        id="pp-invite-completer"
                        className="fi"
                        value={inviteCompletingAs}
                        onChange={(e) => setInviteCompletingAs(e.target.value)}
                        style={{ cursor: 'pointer' }}
                    >
                        <option value="self">The client (person seeking advice)</option>
                        <option value="family_member">A family member</option>
                        <option value="epoa">An enduring power of attorney</option>
                        <option value="professional">A professional completing with the client</option>
                    </select>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5, lineHeight: 1.5 }}>
                        Matches your existing client portal options. The client&apos;s link can pre-answer this question so they go straight to entering their details.
                    </div>
                </div>
                {/* The client's own name is a profile answer (step 2) — and for a couple there are two — so it is not
                    asked here (owner, 16 Oct 2026). The file is named from the profile once those are in. */}
                {inviteCompletingAs !== 'self' && (
                    <div className="mfl" style={{ marginTop: 4 }}>Who will complete it? The link goes to them.</div>
                )}
                <div className="m2">
                    <div className="mfg">
                        <label className="mfl" htmlFor="pp-invite-fname">
                            {inviteCompletingAs === 'self' ? 'First Name' : 'Their First Name'}
                        </label>
                        <input id="pp-invite-fname" className="fi" placeholder="Helen" value={inviteFirstName} onChange={(e) => { setInviteFirstName(e.target.value); setInviteProblem(null); }} />
                    </div>
                    <div className="mfg">
                        <label className="mfl" htmlFor="pp-invite-lname">
                            {inviteCompletingAs === 'self' ? 'Last Name' : 'Their Last Name'}
                        </label>
                        <input id="pp-invite-lname" className="fi" placeholder="Tan" value={inviteLastName} onChange={(e) => setInviteLastName(e.target.value)} />
                    </div>
                </div>
                {inviteCompletingAs !== 'self' && (
                    <div className="mfg">
                        <label className="mfl" htmlFor="pp-invite-relationship">
                            Their relationship to the client
                        </label>
                        <input id="pp-invite-relationship" className="fi" placeholder={inviteCompletingAs === 'professional' ? 'Accountant' : 'Daughter'} value={inviteRelationship} onChange={(e) => setInviteRelationship(e.target.value)} />
                    </div>
                )}
                <div className="mfg">
                    <label className="mfl" htmlFor="pp-invite-email">
                        Email
                    </label>
                    <input
                        id="pp-invite-email"
                        className="fi"
                        type="email"
                        placeholder="client@email.com"
                        value={inviteEmail}
                        onChange={(e) => { setInviteEmail(e.target.value); setInviteProblem(null); }}
                    />
                    {/* Plenty of aged care clients have no email at all, and this path genuinely
                        cannot serve them: the invite IS a link sent to an address, and accepting it
                        verifies the signed-in person against that address. Rather than leave the
                        adviser with a disabled button and no explanation, point at the path that
                        does work — where an email is optional. */}
                    <div className="ms" style={{ marginTop: 6 }}>
                        Required — the secure link is sent here. If your client doesn&apos;t have an email address,
                        close this and use <strong>New Client (In-Person)</strong> instead: you fill the profile in
                        together and no email is needed.
                    </div>
                </div>
                <div className="mfg">
                    <label className="mfl" htmlFor="pp-invite-mobile">
                        Mobile number
                    </label>
                    <input
                        id="pp-invite-mobile"
                        className="fi"
                        type="tel"
                        placeholder="04xx xxx xxx"
                        autoComplete="tel"
                        value={inviteMobile}
                        onChange={(e) => setInviteMobile(e.target.value)}
                    />
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5, lineHeight: 1.5 }}>
                        Optional. Saved on the client’s profile as their preferred phone number, so you can contact them. We do not send text messages.
                    </div>
                </div>
                {/*
                    Two settings, two equal columns, and NOTHING under either of them. A note inside
                    one column stretches it, drops the other control to the foot of the row, and
                    squeezes both selects into half the width they need — which is what made this
                    corner of the form look broken however the alignment was set. Anything that has to
                    be said now spans the full width, below.
                */}
                <div className="m2">
                    <div className="mfg">
                        <label className="mfl" htmlFor="pp-invite-access">
                            Access Mode
                        </label>
                        <select
                            id="pp-invite-access"
                            className="fi"
                            value={inviteAccessMode}
                            onChange={(e) => setInviteAccessMode(e.target.value)}
                            style={{ cursor: 'pointer' }}
                        >
                            <option value="client_stop_dashboard">Adviser-led — profile only</option>
                            <option value="client_end_to_end">Self-serve — full journey</option>
                        </select>
                    </div>
                    <div className="mfg">
                        {/* "if no response" was doing no work — a follow-up is by definition what
                            happens when nobody has replied. */}
                        <label className="mfl" htmlFor="pp-invite-followup">
                            Follow-up after
                        </label>
                        <select
                            id="pp-invite-followup"
                            className="fi"
                            value={inviteAutoFollowUp}
                            onChange={(e) => setInviteAutoFollowUp(e.target.value)}
                            style={{ cursor: 'pointer' }}
                        >
                            <option value="3">3 days</option>
                            <option value="5">5 days</option>
                            <option value="7">7 days</option>
                            <option value="manual">Manual only — I&apos;ll reach out myself</option>
                        </select>
                    </div>
                </div>
                {/*
                    Said only when the control would otherwise be misleading.
                    
                    Chasing ON is the normal case and needs no commentary — "follow-up after 7 days"
                    already says it. Chasing OFF does need a line, because the adviser has just chosen
                    an interval that nothing will act on, and finding that out a fortnight later when a
                    client went quiet is exactly the failure this setting was built to prevent.
                */}
                {inviteAutoFollowUp === 'manual' ? (
                    <div className="ms" style={{ marginTop: -4, marginBottom: 14 }}>
                        They&apos;ll appear in your <strong>Follow-ups</strong> list for you to chase.
                    </div>
                ) : autoReminders && !autoReminders.enabled ? (
                    <div className="ms" style={{ marginTop: -4, marginBottom: 14 }}>
                        Automatic follow-ups are switched off on this server — they&apos;ll appear in your{' '}
                        <strong>Follow-ups</strong> list instead.
                    </div>
                ) : null}
                <div className="mfoot">
                    <button type="button" className="bsm bg" onClick={() => setModal(null)}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="bsm bp"
                        aria-disabled={!isIntakeReady(inviteFirstName, inviteEmail, true)}
                        onClick={() => {
                            // Never a silently dead button: say what is missing and put the cursor there.
                            if (!inviteFirstName.trim()) {
                                setInviteProblem("Enter the client's first name.");
                                document.getElementById('pp-invite-fname')?.focus();
                                return;
                            }
                            if (!isIntakeReady(inviteFirstName, inviteEmail, true)) {
                                setInviteProblem(inviteEmail.trim() ? 'That email address does not look right — check it and try again.' : "Enter the client's email address — the secure link is sent there.");
                                document.getElementById('pp-invite-email')?.focus();
                                return;
                            }
                            setInviteProblem(null);
                            onCreateInviteCase?.({
                                completingAs: inviteCompletingAs,
                                firstName: inviteFirstName,
                                lastName: inviteLastName,
                                relationship: inviteRelationship,
                                email: inviteEmail,
                                mobile: inviteMobile,
                                accessMode: inviteAccessMode,
                                autoFollowUp: inviteAutoFollowUp,
                            });
                            setModal(null);
                            resetInviteForm();
                        }}
                    >
                        Send Invite →
                    </button>
                </div>
                {inviteProblem && (
                    <div className="ms" role="alert" style={{ color: 'var(--red)', fontWeight: 600, textAlign: 'right', marginTop: 8 }} data-testid="invite-problem">
                        {inviteProblem}
                    </div>
                )}
            </div>
        </div>
    );

    const renderInPersonModal = () => (
        <div className={`mdrop${modal === 'inperson' ? ' on' : ''}`} role="presentation" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
            <div className="modal" style={{ maxWidth: 460 }} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="mclose" onClick={() => setModal(null)} aria-label="Close">
                    ×
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                    <div
                        style={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg,var(--blue),var(--cyan))',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, stroke: '#fff', fill: 'none', strokeWidth: 2.5 }}>
                            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <line x1="19" y1="8" x2="19" y2="14" />
                            <line x1="22" y1="11" x2="16" y2="11" />
                        </svg>
                    </div>
                    <div className="mt" style={{ margin: 0 }}>
                        New Client — In-Person Session
                    </div>
                </div>
                <div className="ms">The client is with you now. Create their record and go straight into the profile fact-find together.</div>
                <div
                    style={{
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: 'var(--r)',
                        padding: '10px 14px',
                        fontSize: 12,
                        color: '#1e40af',
                        marginBottom: 20,
                        lineHeight: 1.6,
                    }}
                >
                    This creates the engagement with status <strong>In Progress</strong>. Details below pre-fill Step 2 of the client profile.
                </div>
                <div className="m2">
                    <div className="mfg">
                        <label className="mfl" htmlFor="pp-ip-fname">
                            First Name
                        </label>
                        <input
                            id="pp-ip-fname"
                            className="fi"
                            placeholder="e.g. Margaret"
                            value={inPersonFirstName}
                            onChange={(e) => setInPersonFirstName(e.target.value)}
                        />
                    </div>
                    <div className="mfg">
                        <label className="mfl" htmlFor="pp-ip-lname">
                            Last Name
                        </label>
                        <input
                            id="pp-ip-lname"
                            className="fi"
                            placeholder="e.g. Collins"
                            value={inPersonLastName}
                            onChange={(e) => setInPersonLastName(e.target.value)}
                        />
                    </div>
                </div>
                <div className="mfg">
                    <label className="mfl" htmlFor="pp-ip-email">
                        Email <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(optional)</span>
                    </label>
                    <input
                        id="pp-ip-email"
                        className="fi"
                        type="email"
                        placeholder="client@email.com"
                        value={inPersonEmail}
                        onChange={(e) => setInPersonEmail(e.target.value)}
                    />
                </div>
                <div className="mfg">
                    <label className="mfl" htmlFor="pp-ip-phone">
                        Preferred phone number
                    </label>
                    <input
                        id="pp-ip-phone"
                        className="fi"
                        type="tel"
                        placeholder="e.g. 04xx xxx xxx or 02 xxxx xxxx"
                        value={inPersonPhone}
                        onChange={(e) => setInPersonPhone(e.target.value)}
                    />
                </div>
                <div className="mfg">
                    <label className="mfl" htmlFor="pp-ip-completer">
                        Who is providing the information?
                    </label>
                    <select
                        id="pp-ip-completer"
                        className="fi"
                        value={inPersonCompletingAs}
                        onChange={(e) => setInPersonCompletingAs(e.target.value)}
                        style={{ cursor: 'pointer' }}
                    >
                        <option value="self">The client (person seeking advice)</option>
                        <option value="family_member">A family member</option>
                        <option value="epoa">An enduring power of attorney</option>
                    </select>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5, lineHeight: 1.4 }}>
                        All other details are captured in the profile. Data should reflect the end client&apos;s situation regardless of who is filling it in.
                    </div>
                </div>
                <div className="mfoot">
                    <button type="button" className="bsm bg" onClick={() => setModal(null)}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="bsm bp"
                        disabled={!isIntakeReady(inPersonFirstName, inPersonEmail, false)}
                        title={inPersonFirstName.trim() ? undefined : "Enter the client's first name."}
                        onClick={() => {
                            onCreateInPersonCase?.({
                                firstName: inPersonFirstName,
                                lastName: inPersonLastName,
                                email: inPersonEmail,
                                preferredPhone: inPersonPhone,
                                completingAs: inPersonCompletingAs,
                            });
                            setModal(null);
                            resetInPersonForm();
                        }}
                    >
                        Start Profile →
                    </button>
                </div>
            </div>
        </div>
    );

    const renderAddUserModal = () => (
        <div className={`mdrop${modal === 'adduser' ? ' on' : ''}`} role="presentation" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
            <div className="modal" style={{ maxWidth: 440 }} role="dialog" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="mclose" onClick={() => setModal(null)}>
                    ×
                </button>
                <div className="mt">Add Team Member</div>
                <div className="ms">Invite an adviser or accountant to this practice (demo).</div>
                <div className="mfoot">
                    <button type="button" className="bsm bg" onClick={() => setModal(null)}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );

    const renderAddPracticeModal = () => (
        <div className={`mdrop${modal === 'addprac' ? ' on' : ''}`} role="presentation" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
            <div className="modal" role="dialog" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="mclose" onClick={() => setModal(null)}>
                    ×
                </button>
                <div className="mt">Onboard New Practice</div>
                <div className="ms">Send a subscription invite to a new firm under your licence (demo).</div>
                <div className="mfoot">
                    <button type="button" className="bsm bg" onClick={() => setModal(null)}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );

    const adviserDashboard = (
        <>
            {/* Real firms only. The demo portal opens with a full sample book, so a checklist telling
                somebody to invite their first client would be describing a screen they are not on. */}
            {realIdentity?.tenantId && (
                <FirstRunChecklist
                    hasClient={cases.length > 0}
                    firmProfileDone={Boolean(realIdentity.firmProfileComplete)}
                    isAdviserFirm={realIdentity.profession === 'adviser'}
                    isPrincipal={realIdentity.role === 'owner'}
                    onInviteClient={() => setModal('invite')}
                    onNewInPerson={() => setModal('inperson')}
                    onOpenSettings={() => setView(V.advSettings)}
                    dismissed={setupDismissed}
                    onDismiss={dismissSetup}
                />
            )}
            <div className="krow">
                <div className="kcard">
                    <div className="kl">My Active Clients</div>
                    <div className="kv">{metrics.active}</div>
                    <div className="ks g">In scope</div>
                </div>
                <div className="kcard">
                    <div className="kl">Awaiting Data</div>
                    <div className="kv">{metrics.awaiting}</div>
                    <div className="ks a">Invited + in progress</div>
                </div>
                <div className="kcard">
                    <div className="kl">Ready to Model</div>
                    <div className="kv">{metrics.ready}</div>
                    <div className="ks g">
                        {metrics.inModelling > 0
                            ? `${metrics.awaitingModelling} to pick up · ${metrics.inModelling} in modelling`
                            : 'Profiles submitted'}
                    </div>
                </div>
                <div className="kcard">
                    <div className="kl">SOAs Delivered</div>
                    <div className="kv">{metrics.soaPub}</div>
                    <div className="ks">{metrics.soaDraft > 0 ? `Published · ${metrics.soaDraft} in draft` : 'Published'}</div>
                </div>
            </div>
            <div className="sh">
                <div className="sh-t">My Client Pipeline</div>
                <div className="fps">
                    <button type="button" className={`fp${pipelineFilter === 'all' ? ' on' : ''}`} onClick={() => setPipelineFilter('all')}>
                        All
                    </button>
                    <button type="button" className={`fp${pipelineFilter === 'overdue' ? ' on' : ''}`} onClick={() => setPipelineFilter('overdue')}>
                        Overdue
                    </button>
                    <button type="button" className={`fp${pipelineFilter === 'ready' ? ' on' : ''}`} onClick={() => setPipelineFilter('ready')}>
                        Ready
                    </button>
                </div>
            </div>
            {/* Four columns, not five. There WAS a leading "Draft / Not Invited Yet" column, but
                nothing could ever put a client in it: both intake paths land further along — "Invite
                Client" creates the file and sends the link (Invite Sent), and "New Client (In-Person)"
                opens the profile there and then (Filling In Profile). A permanently empty column that
                looks like a stage is worse than no column. Demo cases with that status are folded into
                Invite Sent below. */}
            <div className="pipe pp-pipe-4">
                <div className="pcol">
                    <div className="pch">
                        <div className="pcl">
                            <div className="pd" style={{ background: '#3b82f6' }} />
                            Invite Sent
                        </div>
                        <div className="pcn">{pipelineSentCases.length}</div>
                    </div>
                    {pipelineSentCases.slice(0, 5).map((c) => {
                        const d = daysSinceIso(c.lastActivityAt);
                        return (
                            <PipelineCard key={c.id} title={c.clientName} subtitle={d != null ? `Invited ${d}d ago — not started` : 'Invited — not started'}>
                                <div className="pbar">
                                    <div className="pfill" style={{ width: 0 }} />
                                </div>
                                <div className="ccf">
                                    <span className="tag t-st">{d != null && d > 7 ? `${d}d no activity` : 'Awaiting client'}</span>
                                    <button
                                        type="button"
                                        className="ma"
                                        disabled={!onRemindClient || !c.workflow?.inviteId}
                                        title={c.workflow?.inviteId ? 'Send this client a fresh link by email — the earlier one stops working' : 'No outstanding invite to resend'}
                                        onClick={() => onRemindClient?.(c.id)}
                                    >
                                        Nudge
                                    </button>
                                </div>
                            </PipelineCard>
                        );
                    })}
                </div>
                <div className="pcol">
                    <div className="pch">
                        <div className="pcl">
                            <div className="pd" style={{ background: '#f59e0b' }} />
                            Filling In Profile
                        </div>
                        <div className="pcn">{pipelineProgressCases.length}</div>
                    </div>
                    {pipelineProgressCases.slice(0, 8).map((c) => (
                        <PipelineCard key={c.id} title={c.clientName} subtitle={progressLabel(c)}>
                            <div className="pbar">
                                <div className="pfill" style={{ width: `${c.profileProgressPct || 0}%`, background: '#f59e0b' }} />
                            </div>
                            <div className="ccf">
                                <span className="tag t-pr">{c.profileProgressIsEstimated ? '~' : ''}{c.profileProgressPct || 0}%</span>
                                <button type="button" className="ma go" onClick={() => onOpenCaseJourney(c.id)}>
                                    Open →
                                </button>
                            </div>
                        </PipelineCard>
                    ))}
                </div>
                <div className="pcol">
                    <div className="pch">
                        <div className="pcl">
                            <div className="pd" style={{ background: '#10b981' }} />
                            Ready to Model
                        </div>
                        <div className="pcn">{pipelineReadyCases.length}</div>
                    </div>
                    {pipelineReadyCases.slice(0, 5).map((c) => (
                        <PipelineCard key={c.id} title={c.clientName} subtitle={`Submitted ${formatSubmittedLabel(c)}`} highlight>
                            <div className="pbar">
                                <div className="pfill g" style={{ width: '100%' }} />
                            </div>
                            <div className="ccf">
                                <span className="tag t-ok">{(c.savedModelCount || 0) > 0 ? `${c.savedModelCount} modelled` : 'Profile done'}</span>
                                <button type="button" className="ma go" onClick={() => onOpenCaseJourney(c.id)}>
                                    Open →
                                </button>
                            </div>
                        </PipelineCard>
                    ))}
                </div>
                <div className="pcol soa-col">
                    <div className="pch">
                        <div className="pcl">
                            <div className="pd" style={{ background: '#6366f1' }} />
                            Advice (SOA)
                        </div>
                        <div className="pcn">{pipelineSoaCases.length}</div>
                    </div>
                    {pipelineSoaCases.slice(0, 5).map((c) => (
                        <PipelineCard
                            key={c.id}
                            title={c.clientName}
                            subtitle={(c.soaStatus === 'published' || c.status === 'soa_published') ? 'SOA sent to client' : 'SOA in draft'}
                            highlight={c.soaStatus === 'published' || c.status === 'soa_published'}
                        >
                            <div className="ccf">
                                <button type="button" className="ma go" onClick={() => onOpenCaseFromModel(c.id)}>
                                    {(c.soaStatus === 'published' || c.status === 'soa_published') ? 'View' : 'Edit & Send →'}
                                </button>
                            </div>
                        </PipelineCard>
                    ))}
                    {pipelineSoaCases.length === 0 && <div className="ccm" style={{ padding: '8px 0' }}>No SOAs yet</div>}
                </div>
            </div>
            <div className="tw">
                <div className="thdr">
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--dark)' }}>All My Clients</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button
                            type="button"
                            className="bsm bg"
                            style={{ fontSize: 12, padding: '5px 11px' }}
                            onClick={exportClientsCsv}
                            disabled={clientTableFilteredCases.length === 0}
                        >
                            Export
                        </button>
                        <input className="si" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                </div>
                {clientTableFilterBar}
                <table>
                    <thead>
                        <tr>
                            <th>Client</th>
                            <th>Type</th>
                            <th>Profile Status</th>
                            <th>SOA Status</th>
                            <th>Last Activity</th>
                            <th>Access Mode</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {clientTableFilteredCases.map((c) => {
                            const pt = profileTag(c);
                            const st = soaTag(c);
                            const isCouple = c.factFind?.adviceFor === 'couple' || (c.clientName && c.clientName.includes('&'));
                            const lastD = daysSinceIso(c.lastActivityAt);
                            return (
                                <tr key={c.id}>
                                    <td>
                                        <div className="tn">
                                            {c.clientName}
                                            {c.visibility === 'restricted' && (
                                                <span
                                                    title="Only you and the principal can see this client"
                                                    style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 4, padding: '1px 5px', verticalAlign: 1 }}
                                                >
                                                    restricted
                                                </span>
                                            )}
                                        </div>
                                        <div className="te">{c.clientEmail}</div>
                                        {c.clientMobile ? <div className="te">{c.clientMobile}</div> : null}
                                    </td>
                                    <td className="tm">{isCouple ? 'Couple' : 'Single'}</td>
                                    <td>
                                        <span className={pt.cls}>{pt.label}</span>
                                    </td>
                                    <td>
                                        <span className={st.cls} style={st.style}>
                                            {st.label}
                                        </span>
                                    </td>
                                    <td className={lastD != null && lastD > 8 ? 'tov' : 'tm'}>{formatLastActivityDisplay(c.lastActivityAt)}</td>
                                    <td>
                                        <span className={accessModeClass(c.accessMode)}>{accessModeLabel(c.accessMode)}</span>
                                    </td>
                                    <td>
                                        <div className="abtns">
                                            <button type="button" className="ab p" onClick={() => onOpenCaseJourney(c.id)}>
                                                Open Model →
                                            </button>
                                            {hasReviewableSoa(c) && (
                                                <button type="button" className="ab" onClick={() => onOpenCaseFromModel(c.id)}>
                                                    Review
                                                </button>
                                            )}
                                            {realIdentity?.tenantId && c.isReal && (
                                                <ClientRowActions
                                                    profileId={c.id}
                                                    clientName={c.clientName}
                                                    adviserUserId={c.adviserUserId ?? null}
                                                    visibility={c.visibility ?? 'firm'}
                                                    tenantId={realIdentity.tenantId}
                                                    myUserId={realIdentity.userId ?? ''}
                                                    isPrincipal={realIdentity.role === 'owner'}
                                                    onChanged={onClientAssignmentChanged}
                                                />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );

    const adviserFollowups = (
        <>
            <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>My Follow-ups</h2>
            <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 18 }}>Everything waiting on someone — clients yet to start or stalled part-way, and work sitting with you: profiles ready to model, and SOAs drafted but never sent. Longest wait first.</p>
            <div className="tw">
                <table>
                    <thead>
                        <tr>
                            <th>Client</th>
                            <th>Status</th>
                            <th>Currently On</th>
                            <th>Days Inactive</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {followupRows.map(({ c, kind, waitingOn, days: d }) => {
                            const pres = FOLLOWUP_PRESENTATION[kind];
                            return (
                                <tr key={c.id}>
                                    <td>
                                        <div className="tn">{c.clientName}</div>
                                        <div className="te">{pres.detail}</div>
                                    </td>
                                    <td>
                                        <span className={pres.chipCls}>{pres.chip}</span>
                                    </td>
                                    <td className="tm">{c.status === 'started' ? progressLabel(c) : waitingOn === 'firm' ? 'With you' : '—'}</td>
                                    <td className={d != null && d > 5 ? 'tov' : 'tm'}>{d != null ? `${d} days` : '—'}</td>
                                    <td>
                                        <div className="abtns">
                                            {/* Firm-side rows open the client's file — the action that clears
                                                them. Client-side rows resend the client's link; only possible
                                                where there IS an invite (a client added in person has none). */}
                                            <button
                                                type="button"
                                                className="ab p"
                                                disabled={waitingOn !== 'firm' && !c.workflow?.inviteId}
                                                title={
                                                    waitingOn === 'firm' || c.workflow?.inviteId
                                                        ? undefined
                                                        : 'This client was added in person, so there is no invite to resend.'
                                                }
                                                onClick={
                                                    waitingOn === 'firm'
                                                        ? () => onOpenCaseJourney(c.id)
                                                        : () => onRemindClient?.(c.id)
                                                }
                                            >
                                                {pres.action}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );

    const activityEvents = useMemo(() => buildActivityFeed(filteredCases), [filteredCases]);

    const focusCaseRow = focusedCaseId ? cases.find((x) => x.id === focusedCaseId) : null;

    const adviserSoas = (
        <>
            <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>Reports & Digital SOAs</h2>
            {role === 'relationship_owner' && focusCaseRow && (
                <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16, lineHeight: 1.5 }}>
                    <strong style={{ color: 'var(--dark)' }}>Review:</strong> {focusCaseRow.clientName} — draft / published SOA work happens here (demo). Use{' '}
                    <strong>Open Model →</strong> from <em>All My Clients</em> to open the full client journey.
                </p>
            )}
            <div className="tw">
                <table>
                    <thead>
                        <tr>
                            <th>Client</th>
                            <th>SOA Status</th>
                            <th>Last activity</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredCases
                            .filter((c) => c.soaStatus !== 'none' || ['submitted', 'adviser_review', 'soa_draft', 'soa_published'].includes(c.status))
                            .map((c) => (
                                <tr
                                    key={c.id}
                                    style={
                                        focusedCaseId && c.id === focusedCaseId
                                            ? { background: 'rgba(59, 130, 246, 0.09)', boxShadow: 'inset 3px 0 0 0 #3b82f6' }
                                            : undefined
                                    }
                                >
                                    <td className="tn">{c.clientName}</td>
                                    <td>
                                        {(c.soaStatus === 'published' || c.status === 'soa_published')
                                            ? <span className="tag t-ok">Published</span>
                                            : (c.soaStatus === 'draft' || c.status === 'soa_draft')
                                                ? <span className="tag t-pr">Draft</span>
                                                : <span className="tag t-sn">Not started</span>}
                                    </td>
                                    <td className="tm">{formatLastActivityDisplay(c.lastActivityAt)}</td>
                                    <td>
                                        {(() => {
                                            const caseSoas = soaByCaseId.get(c.id) || [];
                                            const selectedId = c.currentSoaId
                                                || caseSoas.find((s) => s.isCurrentForCase)?.id
                                                || caseSoas[0]?.id
                                                || '';
                                            const selectedSoa = caseSoas.find((s) => s.id === selectedId) || null;
                                            const selectedStatus = selectedSoa?.soaDeliveryStatus || ((c.soaStatus === 'published' || c.status === 'soa_published') ? 'published' : 'draft');
                                            const isDraftSelected = selectedStatus === 'draft';
                                            return (
                                                <div className="abtns" style={{ gap: 6, flexWrap: 'wrap' }}>
                                                    <button type="button" className="ab p" onClick={() => onOpenCaseFromModel(c.id)}>
                                                        Open →
                                                    </button>
                                                    {caseSoas.length > 0 && (
                                                        <select
                                                            value={selectedId}
                                                            onChange={(e) => onSetCurrentSoaForCase?.(c.id, e.target.value)}
                                                            className="ab"
                                                            style={{ minWidth: 210 }}
                                                        >
                                                            {caseSoas.map((soa) => (
                                                                <option key={soa.id} value={soa.id}>
                                                                    {soa.name} ({(soa.soaDeliveryStatus || 'draft') === 'published' ? 'Published' : 'Draft'})
                                                                    {soa.registerOnly ? ' · from register' : ''}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    )}
                                                    {selectedSoa && isDraftSelected && (
                                                        <button type="button" className="ab" onClick={() => onMarkSoaSent?.(c.id, selectedSoa.id)}>
                                                            Sent to Client
                                                        </button>
                                                    )}
                                                    {/* No delete for a register-only version: the advice register never erases
                                                        history (the soas table has no DELETE grant at all), so offering it would
                                                        be a button that cannot work. */}
                                                    {selectedSoa && isDraftSelected && !selectedSoa.registerOnly && (
                                                        <button type="button" className="ab" onClick={() => onDeleteDraftSoa?.(selectedSoa.id)}>
                                                            Delete Draft
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        </>
    );

    /**
     * Activity Feed — what has actually happened across this adviser's clients, newest first.
     *
     * The sidebar had this item pointing at the Dashboard: a link to a screen that did not exist.
     * Built from timestamps the client list already carries, so it costs no extra requests. See
     * activityFeed.ts for what it can and cannot show (latest-of-each-kind, not a full audit trail).
     */
    const adviserActivity = (
        <>
            <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>Activity Feed</h2>
            <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 18 }}>
                Recent milestones across your clients — what they have done, and what you have. Most recent first.
            </p>
            {activityEvents.length === 0 ? (
                <div className="ccm" style={{ padding: '14px 0' }}>
                    Nothing yet. Milestones appear here as clients are invited, fill in their profile, and you model
                    their options and issue advice.
                </div>
            ) : (
                <div className="tw">
                    <table>
                        <thead>
                            <tr>
                                <th>Client</th>
                                <th>What happened</th>
                                <th>Who</th>
                                <th>When</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {activityEvents.map((ev) => (
                                <tr key={`${ev.caseId}-${ev.kind}`}>
                                    <td className="tn">{ev.clientName}</td>
                                    <td>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                            <span
                                                className="pd"
                                                style={{ background: ACTIVITY_PRESENTATION[ev.kind].colour, flex: '0 0 auto' }}
                                            />
                                            {ev.label}
                                        </span>
                                    </td>
                                    <td className="tm">{ev.actor === 'client' ? 'Client' : 'You'}</td>
                                    <td className="tm">{formatLastActivityDisplay(ev.at)}</td>
                                    <td>
                                        <div className="abtns">
                                            <button type="button" className="ab p" onClick={() => onOpenCaseJourney(ev.caseId)}>
                                                Open →
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );

    const adviserAnalytics = <AdviserAnalyticsView cases={cases} dataState={analyticsDataState} />;

    const adviserSettings = (
        <AdviserSettingsView identity={realIdentity} firmSettings={firmSettings} messageDefaults={messageDefaults} onSaveFirmSettings={onSaveFirmSettings} />
    );

    const practiceDashboard = (
        <>
            <div className="bok">
                <svg viewBox="0 0 24 24">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <strong style={{ marginRight: 4 }}>{metrics.ready} clients</strong> ready for modelling across your practice.
                <button type="button" style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: '#065f46', textDecoration: 'underline', background: 'none', border: 'none' }} onClick={() => setView(V.pracClients)}>
                    View →
                </button>
            </div>
            <div className="bwarn">
                <svg viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <strong style={{ marginRight: 4 }}>{metrics.followups} clients</strong> may need follow-up across the practice.
                <button type="button" style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: '#92400e', textDecoration: 'underline', background: 'none', border: 'none' }} onClick={() => setView(V.pracFollow)}>
                    Manage →
                </button>
            </div>
            <div className="krow">
                <div className="kcard">
                    <div className="kl">Advisers / CROs</div>
                    <div className="kv">6</div>
                    <div className="ks">Demo team</div>
                </div>
                <div className="kcard">
                    <div className="kl">Active Client Cases</div>
                    <div className="kv">{metrics.total}</div>
                    <div className="ks g">In this practice</div>
                </div>
                <div className="kcard">
                    <div className="kl">Awaiting Data</div>
                    <div className="kv">{metrics.awaiting}</div>
                    <div className="ks a">Invited + in progress</div>
                </div>
                <div className="kcard">
                    <div className="kl">Completed This Month</div>
                    <div className="kv">{metrics.ready}</div>
                    <div className="ks g">Submitted profiles</div>
                </div>
            </div>
            <div className="sh">
                <div className="sh-t">Advisers & Accountants</div>
                <button type="button" className="bsm bp" style={{ fontSize: 12, padding: '5px 11px' }} onClick={() => setModal('adduser')}>
                    + Add Team Member
                </button>
            </div>
            <div style={{ background: '#fff', border: '1px solid var(--bdr)', borderRadius: 'var(--rl)', padding: '4px 15px', marginBottom: 22 }}>
                {['Joncarl La Rosa', 'Sarah Chen', 'Raj Patel'].map((name, i) => (
                    <div className="ar" key={name}>
                        <div className="ar-ava" style={{ background: ['linear-gradient(135deg,#1a6fd4,#17b8e0)', 'linear-gradient(135deg,#7c3aed,#a78bfa)', 'linear-gradient(135deg,#c2410c,#fb923c)'][i] }}>
                            {name
                                .split(' ')
                                .map((p) => p[0])
                                .join('')}
                        </div>
                        <div className="ar-info">
                            <div className="ar-name">{name}</div>
                            <div className="ar-meta">
                                {i === 2 ? (
                                    <>
                                        Accountant · <span className="mtag mt-acc">Accountant</span>{' '}
                                        <span style={{ fontSize: 10, color: 'var(--amb)' }}>⚠ Facilitator only</span>
                                    </>
                                ) : (
                                    <>
                                        Financial Adviser{i === 0 ? ' · CFP' : ''} · <span className="mtag mt-adv">Adviser</span>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="ar-stats">
                            <div className="ast">
                                <div className="astv">{i === 0 ? 8 : i === 1 ? 6 : 5}</div>
                                <div className="astl">Clients</div>
                            </div>
                            <div className="ast">
                                <div className="astv" style={{ color: 'var(--grn)' }}>
                                    {i === 0 ? 3 : i === 1 ? 2 : 1}
                                </div>
                                <div className="astl">Done</div>
                            </div>
                            <div className="ast">
                                <div className="astv" style={{ color: i === 2 ? 'var(--dark)' : 'var(--amb)' }}>
                                    {i === 0 ? 2 : i === 1 ? 1 : 0}
                                </div>
                                <div className="astl">Overdue</div>
                            </div>
                        </div>
                        <div style={{ marginLeft: 12 }}>
                            <button type="button" className="ab">
                                View →
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );

    const practiceClientsTable = (
        <>
            <div className="sh" style={{ marginBottom: 14 }}>
                <div className="sh-t">All Client Engagements</div>
                <button type="button" className="bsm bp" style={{ fontSize: 12 }} onClick={() => setModal('invite')}>
                    + New Engagement
                </button>
            </div>
            <div className="tw">
                <div className="thdr">
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--dark)' }}>{filteredCases.length} total</div>
                    <input className="si" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                {clientTableFilterBar}
                <table>
                    <thead>
                        <tr>
                            <th>Client</th>
                            <th>Adviser</th>
                            <th>Status</th>
                            <th>Last Activity</th>
                            <th>Access</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {clientTableFilteredCases.map((c) => {
                            const pt = profileTag(c);
                            return (
                                <tr key={c.id}>
                                    <td>
                                        <div className="tn">{c.clientName}</div>
                                        <div className="te">{c.clientEmail}</div>
                                        {c.clientMobile ? <div className="te">{c.clientMobile}</div> : null}
                                    </td>
                                    <td className="tm">{c.ownerName}</td>
                                    <td>
                                        <span className={pt.cls}>{pt.label}</span>
                                    </td>
                                    <td className="tm">{formatLastActivityDisplay(c.lastActivityAt)}</td>
                                    <td>
                                        <span className={accessModeClass(c.accessMode)}>{accessModeLabel(c.accessMode)}</span>
                                    </td>
                                    <td>
                                        <div className="abtns">
                                            <button type="button" className="ab p" onClick={() => onOpenCaseJourney(c.id)}>
                                                Open →
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );

    const practiceFollow = (
        <>
            <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>Follow-up Centre</h2>
            <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 18 }}>Practice-wide rules and overdue clients across all advisers.</p>
            <div
                style={{
                    background: '#fff',
                    border: '1px solid var(--bdr)',
                    borderRadius: 'var(--rl)',
                    padding: 20,
                    marginBottom: 20,
                }}
            >
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 13 }}>Practice-wide Reminder Rules</div>
                <div className="fur">
                    <div className="ful">First reminder if not opened after</div>
                    <select className="fus" defaultValue="5 days">
                        <option>3 days</option>
                        <option>5 days</option>
                        <option>7 days</option>
                    </select>
                </div>
                <div className="fur">
                    <div className="ful">Second reminder if still inactive</div>
                    <select className="fus" defaultValue="10 days">
                        <option>7 days</option>
                        <option>10 days</option>
                        <option>14 days</option>
                    </select>
                </div>
                <div className="fur">
                    <div className="ful">Alert adviser if stalled after</div>
                    <select className="fus" defaultValue="2 days">
                        <option>2 days</option>
                        <option>3 days</option>
                        <option>5 days</option>
                    </select>
                </div>
                <button type="button" className="bsm bp" style={{ marginTop: 13 }}>
                    Save Rules
                </button>
            </div>
            {adviserFollowups}
        </>
    );

    const practiceSettings = <PracticeSettingsWireframeView />;

    const licenseeDash = (
        <>
            <div className="krow">
                <div className="kcard">
                    <div className="kl">Total Practices</div>
                    <div className="kv">8</div>
                    <div className="ks g">Demo</div>
                </div>
                <div className="kcard">
                    <div className="kl">Total Advisers</div>
                    <div className="kv">34</div>
                    <div className="ks">Across all practices</div>
                </div>
                <div className="kcard">
                    <div className="kl">Active Client Cases</div>
                    <div className="kv">{cases.length}</div>
                    <div className="ks a">All demo cases</div>
                </div>
                <div className="kcard">
                    <div className="kl">Subscriptions</div>
                    <div className="kv" style={{ fontSize: 20 }}>
                        8
                    </div>
                    <div className="ks">Active plans</div>
                </div>
            </div>
            <div className="sh">
                <div className="sh-t">Practices Under Licence</div>
                <div className="fps">
                    <button type="button" className="fp on">
                        All
                    </button>
                    <button type="button" className="fp">
                        Active
                    </button>
                    <button type="button" className="fp">
                        Issues
                    </button>
                </div>
            </div>
            <div className="sum-grid">
                {['La Rosa & Partners', 'Chen & Associates', 'Rivers Wealth'].map((name, idx) => (
                    <div className="scard" key={name}>
                        <div className="sc-top">
                            <div className="sc-name">{name}</div>
                            <span className={idx === 2 ? 'tag t-st' : 'tag t-ok'}>{idx === 2 ? 'Issue' : 'Active'}</span>
                        </div>
                        <div className="tm" style={{ fontSize: 12 }}>
                            {['Sydney', 'Melbourne', 'Brisbane'][idx]} · Demo
                        </div>
                        <div className="sc-mini">
                            <div className="sm">
                                <div className="smv">{4 + idx}</div>
                                <div className="sml">Advisers</div>
                            </div>
                            <div className="sm">
                                <div className="smv">{12 + idx * 6}</div>
                                <div className="sml">Clients</div>
                            </div>
                            <div className="sm">
                                <div className="smv" style={{ color: 'var(--amb)' }}>
                                    {idx}
                                </div>
                                <div className="sml">Overdue</div>
                            </div>
                        </div>
                        <div className="sc-foot">
                            <div className="sc-sub">Renews demo</div>
                            <div className="sc-btn">View practice →</div>
                        </div>
                    </div>
                ))}
                <div className="scard" style={{ border: '1.5px dashed var(--bdr)', boxShadow: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 7 }} onClick={() => setModal('addprac')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setModal('addprac')}>
                    <div style={{ fontSize: 26, color: 'var(--lite)' }}>+</div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--muted)' }}>Onboard New Practice</div>
                </div>
            </div>
            <div className="sh" style={{ marginTop: 28 }}>
                <div className="sh-t">All client engagements (demo)</div>
                <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 12px' }}>
                    Same case list as practice view — use Open → to return to the client journey (Dashboard resumes where you left off).
                </p>
            </div>
            {practiceClientsTable}
        </>
    );

    const licenseeBilling = (
        <>
            <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 18 }}>Billing & Subscriptions</h2>
            <div className="tw">
                <table>
                    <thead>
                        <tr>
                            <th>Practice</th>
                            <th>Advisers</th>
                            <th>Annual Fee</th>
                            <th>Status</th>
                            <th>Renews</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(
                            [
                                ['La Rosa & Partners', '6', 'Active', '12 Mar 2026', false],
                                ['Chen & Associates', '4', 'Active', '5 Jul 2025', false],
                                ['Rivers Wealth', '3', 'Failed', '—', true],
                                ['Northside Planning', '5', 'Active', '22 Aug 2025', false],
                            ] as [string, string, string, string, boolean][]
                        ).map(([name, adv, _st, renew, failed]) => (
                            <tr key={name}>
                                <td className="tn">{name}</td>
                                <td className="tm">{adv}</td>
                                <td className="tm">$2,450</td>
                                <td>
                                    <span className={failed ? 'tag t-st' : 'tag t-ok'}>{failed ? 'Failed' : 'Active'}</span>
                                </td>
                                <td className="tm">{renew}</td>
                                <td>
                                    <div className="abtns">
                                        <button type="button" className={failed ? 'ab p' : 'ab'}>
                                            {failed ? 'Retry Payment' : 'Invoice'}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );

    const licenseeAdvisers = (
        <>
            <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 18 }}>All Advisers Across Licence</h2>
            <div className="tw">
                <table>
                    <thead>
                        <tr>
                            <th>Adviser</th>
                            <th>Practice</th>
                            <th>Role</th>
                            <th>Clients</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <div className="tn">Joncarl La Rosa</div>
                                <div className="te">james@demo.local</div>
                            </td>
                            <td className="tm">La Rosa & Partners</td>
                            <td>
                                <span className="mtag mt-adv">Adviser</span>
                            </td>
                            <td className="tm">8</td>
                            <td>
                                <div className="abtns">
                                    <button type="button" className="ab">
                                        View →
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </>
    );

    const renderMainViews = () => {
        if (role === 'relationship_owner') {
            return (
                <>
                    <div id={V.advDash} className={`pview${activeView === V.advDash ? ' on' : ''}`}>
                        {adviserDashboard}
                    </div>
                    <div id={V.advFollow} className={`pview${activeView === V.advFollow ? ' on' : ''}`}>
                        {adviserFollowups}
                    </div>
                    <div id={V.advSoas} className={`pview${activeView === V.advSoas ? ' on' : ''}`}>
                        {adviserSoas}
                    </div>
                    <div id={V.advSettings} className={`pview${activeView === V.advSettings ? ' on' : ''}`}>
                        {realIdentity ? realFirmSettings : adviserSettings}
                    </div>
                    <div id={V.advActivity} className={`pview${activeView === V.advActivity ? ' on' : ''}`}>
                        {adviserActivity}
                    </div>
                    <div id={V.advAnalytics} className={`pview${activeView === V.advAnalytics ? ' on' : ''}`}>
                        {adviserAnalytics}
                    </div>
                </>
            );
        }
        if (role === 'practice_owner') {
            return (
                <>
                    <div id={V.pracDash} className={`pview${activeView === V.pracDash ? ' on' : ''}`}>
                        {practiceDashboard}
                    </div>
                    <div id={V.pracClients} className={`pview${activeView === V.pracClients ? ' on' : ''}`}>
                        {practiceClientsTable}
                    </div>
                    <div id={V.pracFollow} className={`pview${activeView === V.pracFollow ? ' on' : ''}`}>
                        {practiceFollow}
                    </div>
                    <div id={V.pracSettings} className={`pview${activeView === V.pracSettings ? ' on' : ''}`}>
                        {practiceSettings}
                    </div>
                </>
            );
        }
        return (
            <>
                <div id={V.licDash} className={`pview${activeView === V.licDash ? ' on' : ''}`}>
                    {licenseeDash}
                </div>
                <div id={V.licAdvisers} className={`pview${activeView === V.licAdvisers ? ' on' : ''}`}>
                    {licenseeAdvisers}
                </div>
                <div id={V.licBilling} className={`pview${activeView === V.licBilling ? ' on' : ''}`}>
                    {licenseeBilling}
                </div>
            </>
        );
    };

    // A signed-in firm member is entering: show a branded loading state while their workspace
    // resolves — NEVER the demo login wireframe (owner-reported: real firms saw the demo screen).
    if (realModeLoading && !realIdentity) {
        return (
            <div className="pp-wireframe">
                <div className="pp-auth-screen" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AnimatedBackground />
                    <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', color: '#0f172a' }}>
                        <div
                            style={{
                                width: 44, height: 44, margin: '0 auto 16px', borderRadius: '50%',
                                border: '3px solid #dbeafe', borderTopColor: '#2563eb', animation: 'ppspin 0.8s linear infinite',
                            }}
                        />
                        <p style={{ fontSize: 15, fontWeight: 600 }}>Opening your workspace…</p>
                        <style>{'@keyframes ppspin{to{transform:rotate(360deg)}}'}</style>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="pp-wireframe">
            <div className="pp-portal-root">
                <aside className="sb">
                    <div className="sb-top">
                        <div className="sb-logo">
                            <span className="a">AgedCare</span>
                            <span className="b">Wise</span>
                        </div>
                        <div className="sb-badge">Partner Portal</div>
                    </div>
                    <div className="sb-ctx">
                        <div className="sb-ctx-lbl">Signed in as</div>
                        <span className={`sb-lvl ${cfg.lvlCls}`}>{cfg.lvlLabel}</span>
                        <div className="sb-ctx-name">{cfg.ctxName}</div>
                        <div className="sb-ctx-sub">{cfg.ctxSub}</div>
                    </div>
                    <nav aria-label="Portal">
                        {cfg.nav.map((item, idx) => {
                            if ('sect' in item) return <div key={`s-${idx}`} className="sb-sect">{item.sect}</div>;
                            const badge = item.badgeKey ? badgeCounts[item.badgeKey] : null;
                            const isOn = navActiveId === item.id;
                            return (
                                <button
                                    key={`${item.id}-${item.label}-${idx}`}
                                    type="button"
                                    className={`nl${isOn ? ' on' : ''}`}
                                    onClick={() => setView(item.id)}
                                >
                                    <NavIcon name={item.icon} />
                                    {item.label}
                                    {badge != null ? <span className={`nbadge ${item.badgeCls || ''}`}>{badge}</span> : null}
                                </button>
                            );
                        })}
                    </nav>
                    <div className="sb-foot">
                        <div className="urow">
                            <div className="uava" style={{ background: cfg.avaColor }}>
                                {cfg.avaInit}
                            </div>
                            <div>
                                <div className="un">{cfg.uname}</div>
                                <div className="ur">{cfg.urole}</div>
                            </div>
                        </div>
                        {onOpenAdmin && (
                            <button type="button" className="sb-admin" onClick={onOpenAdmin}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                    <path d="m9 12 2 2 4-4" />
                                </svg>
                                Platform Admin
                            </button>
                        )}
                        <button type="button" className="sb-out" onClick={onExitToConsumer}>
                            Sign out
                        </button>
                    </div>
                </aside>
                <div className="pmain">
                    <div className="topbar">
                        <div className="tb-title">{VIEW_TITLES[activeView] || 'Dashboard'}</div>
                        <div className="bc" aria-hidden="true">
                            {realIdentity ? (
                                <>
                                    <button type="button">{realIdentity.firmName}</button>
                                    <span>/</span>
                                    <button type="button">{realIdentity.adviserName}</button>
                                </>
                            ) : (
                                <>
                                    {role === 'relationship_owner' && (
                                        <>
                                            <button type="button">La Rosa &amp; Partners</button>
                                            <span>/</span>
                                            <button type="button">Joncarl La Rosa</button>
                                        </>
                                    )}
                                    {role === 'practice_owner' && (
                                        <>
                                            <button type="button">Licensee</button>
                                            <span>/</span>
                                            <button type="button">La Rosa &amp; Partners</button>
                                        </>
                                    )}
                                    {role === 'licensee' && <button type="button">Demo Licensee Group</button>}
                                </>
                            )}
                        </div>
                        <div className="tb-r">
                            {/* The bell is the Follow-ups queue; the dot shows only while something is waiting (it used to do nothing — owner, 16 Oct 2026). */}
                            <div
                                className="notif"
                                role="button"
                                tabIndex={0}
                                aria-label={metrics.followups > 0 ? `Follow-ups: ${metrics.followups} waiting` : 'Follow-ups'}
                                title={metrics.followups > 0 ? `${metrics.followups} follow-up${metrics.followups === 1 ? '' : 's'} waiting` : 'Follow-ups'}
                                onClick={() => setView(role === 'practice_owner' ? V.pracFollow : V.advFollow)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setView(role === 'practice_owner' ? V.pracFollow : V.advFollow); } }}
                            >
                                <svg viewBox="0 0 24 24">
                                    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                                    <path d="M13.73 21a2 2 0 01-3.46 0" />
                                </svg>
                                {metrics.followups > 0 && <div className="ndot" />}
                            </div>
                            {cfg.topInvite && (
                                <button type="button" className="bsm bg" onClick={() => setModal('invite')}>
                                    <svg viewBox="0 0 24 24">
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                        <polyline points="22,6 12,13 2,6" />
                                    </svg>
                                    Invite Client
                                </button>
                            )}
                            {cfg.topInPerson && (
                                <button type="button" className="bsm bp" onClick={() => setModal('inperson')}>
                                    <svg viewBox="0 0 24 24">
                                        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                                        <circle cx="9" cy="7" r="4" />
                                        <line x1="19" y1="8" x2="19" y2="14" />
                                        <line x1="22" y1="11" x2="16" y2="11" />
                                    </svg>
                                    New Client (In-Person)
                                </button>
                            )}
                            {cfg.topPracticeExtras && (
                                <button type="button" className="bsm bp" onClick={() => setModal('adduser')}>
                                    Add Team
                                </button>
                            )}
                            {cfg.topLicensee && (
                                <button type="button" className="bsm bp" onClick={() => setModal('addprac')}>
                                    Onboard Practice
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="tabs" role="tablist">
                        {tabsWithBadges.map((t) => (
                            <button key={t.id} type="button" role="tab" className={`tab${activeView === t.id ? ' on' : ''}`} onClick={() => setView(t.id)}>
                                {t.label}
                                {t.badge ? <span style={{ fontSize: 10, background: 'var(--amb)', color: '#fff', borderRadius: 9, padding: '1px 6px', marginLeft: 4 }}>{t.badge}</span> : null}
                            </button>
                        ))}
                    </div>
                    <div className="pscroll">{renderMainViews()}</div>
                </div>
            </div>
            {renderInviteModal()}
            {renderInPersonModal()}
            {renderAddUserModal()}
            {renderAddPracticeModal()}
        </div>
    );
}
